// === Zod schemas: timesheet_hours_record ===
// Per-labourer time entries for all task types (spray, harvest, planting, general,
// pre-ground, etc.) plus standalone manual entries / leave entries.
// Ported from legacy MySQL `timesheet_hours_record`. start_time / finish_time
// are stored as TIMESTAMPTZ (matching the job tables); legacy MySQL TIME values
// are stitched with start_date / finish_date during the data import.

import { z } from 'zod';

export const TimesheetHoursRecordSchema = z.object({
  id:                    z.number().int().positive(),
  annual_leave:          z.string().max(211).nullable(),
  company_id:            z.string().uuid(),
  unpaid_leave:          z.string().max(50),                              // NOT NULL DEFAULT '0'
  staff_id:              z.string().uuid(),
  client:                z.string().max(250).nullable(),
  invoiced:              z.number().int(),                                // NOT NULL DEFAULT 0
  job_address:           z.string().nullable(),
  job_other:             z.string().max(211).nullable(),                  // DEFAULT ''
  job_id:                z.number().int().nullable(),
  task:                  z.string().max(200).nullable(),
  deleted_at:            z.string().datetime({ offset: true }).nullable(),
  start_time:            z.string().datetime({ offset: true }).nullable(),
  finish_time:           z.string().datetime({ offset: true }).nullable(),
  start_date:            z.string().nullable(),                           // YYYY-MM-DD
  finish_date:           z.string().nullable(),
  total_hours:           z.number().nullable(),
  rate_per_hour:         z.number().nullable(),
  cost_labour:           z.number().nullable(),
  overtime_15:           z.number().nullable(),
  overtime_2:            z.number().nullable(),
  total_hours_updated:   z.string().max(211).nullable(),
  is_stop:               z.number().int().nullable(),
  note:                  z.string().nullable(),
  paymaster_note:        z.string().nullable().optional(),
  overtime_21:           z.string().max(211).nullable(),
  public_holiday:        z.string().max(211).nullable(),
  sick_pay:              z.string().max(211).nullable(),
  is_lunch:              z.string().max(11).nullable(),
  staff_title:           z.string().max(200).nullable(),
  client_id:             z.number().int().nullable(),
  staff_name:            z.string().max(150).nullable(),
  labourers:             z.number().int().nullable(),
  trade_school:          z.number().nullable(),
  event_id:              z.number().int().nullable(),
  event_id_sub:          z.string().max(211).nullable(),
  edit_by:               z.string().max(11).nullable(),
  edit_date_time:        z.string().datetime({ offset: true }).nullable(),
  is_manual_entry:       z.number().int(),                                // NOT NULL DEFAULT 0
  created_at:            z.string().datetime({ offset: true }),
  updated_at:            z.string().datetime({ offset: true }),
  staff_count:           z.number().int().nullable(),
  task_type:             z.string().max(100).nullable(),
  block_type:            z.string().max(100).nullable(),
  block_id:              z.number().int().nullable(),
  reference:             z.string().max(100).nullable(),
  add_plants_to_reports: z.number().int().nullable(),
});

// Insert payload (server fills company_id, id, created_at, updated_at, deleted_at).
// staff_id is required since every row belongs to one labourer.
export const TimesheetHoursRecordCreateSchema = z.object({
  staff_id:              z.string().uuid(),
  staff_name:            z.string().max(150).nullable().optional(),
  staff_title:           z.string().max(200).nullable().optional(),
  client:                z.string().max(250).nullable().optional(),
  client_id:             z.number().int().nullable().optional(),
  job_id:                z.number().int().nullable().optional(),
  job_address:           z.string().nullable().optional(),
  job_other:             z.string().max(211).nullable().optional(),
  task:                  z.string().max(200).nullable().optional(),
  task_type:             z.string().max(100).nullable().optional(),
  block_id:              z.number().int().nullable().optional(),
  block_type:            z.string().max(100).nullable().optional(),
  reference:             z.string().max(100).nullable().optional(),
  start_date:            z.string().nullable().optional(),
  finish_date:           z.string().nullable().optional(),
  start_time:            z.string().datetime({ offset: true }).nullable().optional(),
  finish_time:           z.string().datetime({ offset: true }).nullable().optional(),
  total_hours:           z.number().nullable().optional(),
  rate_per_hour:         z.number().nullable().optional(),
  cost_labour:           z.number().nullable().optional(),
  overtime_15:           z.number().nullable().optional(),
  overtime_2:            z.number().nullable().optional(),
  overtime_21:           z.string().max(211).nullable().optional(),
  annual_leave:          z.string().max(211).nullable().optional(),
  unpaid_leave:          z.string().max(50).optional(),
  sick_pay:              z.string().max(211).nullable().optional(),
  public_holiday:        z.string().max(211).nullable().optional(),
  trade_school:          z.number().nullable().optional(),
  is_lunch:              z.string().max(11).nullable().optional(),
  is_stop:               z.number().int().nullable().optional(),
  is_manual_entry:       z.number().int().optional(),
  invoiced:              z.number().int().optional(),
  note:                  z.string().nullable().optional(),
  paymaster_note:        z.string().max(5000).nullable().optional(),
  labourers:             z.number().int().nullable().optional(),
  staff_count:           z.number().int().nullable().optional(),
  event_id:              z.number().int().nullable().optional(),
  event_id_sub:          z.string().max(211).nullable().optional(),
  edit_by:               z.string().max(11).nullable().optional(),
  edit_date_time:        z.string().datetime({ offset: true }).nullable().optional(),
  total_hours_updated:   z.string().max(211).nullable().optional(),
  add_plants_to_reports: z.number().int().nullable().optional(),
});

// Update — every field optional.
export const TimesheetHoursRecordUpdateSchema = TimesheetHoursRecordCreateSchema.partial();

// List query — date-range + filters used by the weekly timesheet view.
// staff_ids is a comma-separated list of labourer UUIDs (matches the same
// shape general_task.labour_id uses).
export const TimesheetHoursRecordListQuerySchema = z.object({
  staff_id:    z.string().uuid().optional(),                  // single, kept for back-compat
  staff_ids:   z.string().optional(),                          // comma-separated UUIDs
  job_id:      z.coerce.number().int().optional(),
  task_type:   z.string().optional(),
  block_type:  z.string().optional(),
  block_id:    z.coerce.number().int().optional(),
  reference:   z.string().optional(),
  date_from:   z.string().optional(),                   // YYYY-MM-DD
  date_to:     z.string().optional(),
  search:      z.string().optional(),
  // Contractor workers (contractor_staff): 'only' = just their hours (optionally
  // one contractor's), 'exclude' = everyone else's (the main Timesheet).
  contractor:    z.enum(['only', 'exclude']).optional(),
  contractor_id: z.coerce.number().int().positive().optional(),
  page:        z.coerce.number().int().positive().optional().default(1),
  per_page:    z.coerce.number().int().positive().max(200).optional().default(50),
});

export type TimesheetHoursRecord       = z.infer<typeof TimesheetHoursRecordSchema>;
export type TimesheetHoursRecordCreate = z.infer<typeof TimesheetHoursRecordCreateSchema>;
export type TimesheetHoursRecordUpdate = z.infer<typeof TimesheetHoursRecordUpdateSchema>;
export type TimesheetHoursRecordListQuery = z.infer<typeof TimesheetHoursRecordListQuerySchema>;
